import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string)?.replace(/\/$/, '');
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export interface ApiKey {
  id: string;
  key_name: string;
  masked_value: string;
  project_id: string | null;
  created_at: string;
}

async function callApiKeys<T>(body: Record<string, unknown>): Promise<{ data?: T; status: number; errorMessage?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { status: 401, errorMessage: 'Session expired, please sign in again' };
  }
  const res = await fetch(`${SUPABASE_URL}/functions/v1/manage-api-keys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  const errMsg = data?.error ?? data?.message;
  if (!res.ok) return { status: res.status, errorMessage: errMsg || `Request failed (${res.status})` };
  if (data?.error) return { status: res.status, errorMessage: data.error };
  return { data: data as T, status: res.status };
}

export function useApiKeys(projectId: string | null) {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const out = await callApiKeys<{ keys?: ApiKey[] }>({ action: 'list', projectId });
      if (out.errorMessage) {
        toast.error(out.errorMessage);
        return;
      }
      setApiKeys(out.data?.keys ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const addApiKey = useCallback(async (keyName: string, keyValue: string): Promise<boolean> => {
    if (!projectId) return false;
    setSaving(true);
    try {
      const out = await callApiKeys<{ error?: string }>({ action: 'set', projectId, keyName, keyValue });
      if (out.errorMessage) {
        toast.error(out.errorMessage);
        return false;
      }
      toast.success(`API key ${keyName} saved`);
      await fetchApiKeys();
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save API key');
      return false;
    } finally {
      setSaving(false);
    }
  }, [projectId, fetchApiKeys]);

  const deleteApiKey = useCallback(async (keyName: string): Promise<boolean> => {
    if (!projectId) return false;
    try {
      const out = await callApiKeys<{ error?: string }>({ action: 'delete', projectId, keyName });
      if (out.errorMessage) {
        toast.error(out.errorMessage);
        return false;
      }
      toast.success(`API key ${keyName} deleted`);
      await fetchApiKeys();
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete API key');
      return false;
    }
  }, [projectId, fetchApiKeys]);

  return { apiKeys, loading, saving, fetchApiKeys, addApiKey, deleteApiKey };
}
